import { Dayjs } from 'dayjs'

import { containsInterval, day, getToday, isAfter, isBefore, overlaps, toDisjointIntervals } from '~/astra'
import { Surgery, SurgeryFolderMetadatum } from '~/storm'
import { getSurgeryMetadata } from '~/utils/surgeryMetadata'
import { assertUnreachable } from '~/utils/utils'

export type AvailabilityPeriod =
    | {
          type: 'availableFrom'
          startDate: Dayjs
      }
    | {
          type: 'unavailableBetween'
          startDate: Dayjs
          endDate: Dayjs
      }

/**
 * This function should do all mapping from SurgeryFolderMetadatum['availability_periods'] to AvailabilityPeriod.
 */
export function getPatientAvailability(patientAvailabilities: SurgeryFolderMetadatum['availability_periods'] | null): AvailabilityPeriod[] {
    function compareByStartDate(a: { startDate: Dayjs }, b: { startDate: Dayjs }) {
        return isBefore(a.startDate, b.startDate, 'day') ? -1 : isAfter(a.startDate, b.startDate, 'day') ? 1 : 0
    }

    // this function doesn't do `return patientAvailabilities.map(...)` because of complicated sorting, implemented manually
    let availableFrom: null | Dayjs = null
    const unavailabilityPeriods = []

    for (const patientAvailability of patientAvailabilities ?? []) {
        switch (patientAvailability.type) {
            case 'available_from':
                if (availableFrom !== null) {
                    console.warn('Multiple available_from in availability_periods')
                }
                availableFrom = day(patientAvailability.start_date)
                break
            case 'unavailable_between':
                unavailabilityPeriods.push({
                    type: 'unavailableBetween' as const,
                    startDate: day(patientAvailability.start_date),
                    endDate: day(patientAvailability.end_date),
                })
                break
            case 'waiting':
                break
            default:
                throw assertUnreachable(patientAvailability, (patientAvailability as { type: string }).type)
        }
    }

    const result: AvailabilityPeriod[] = unavailabilityPeriods.sort(compareByStartDate)
    if (availableFrom) {
        result.unshift({ type: 'availableFrom', startDate: availableFrom })
    }
    return result
}

export function getIsWaiting(surgery: Surgery): boolean {
    return getSurgeryMetadata(surgery)?.availability_periods?.some(metadata => metadata.type === 'waiting') ?? false
}

export function getPatientAvailabilityFromSurgery(surgery: Surgery) {
    return getPatientAvailability(getSurgeryMetadata(surgery)?.availability_periods)
}

export function getPatientAvailabilityFilterValues(
    patientAvailabilityFromFilter?: { label: string; value: string },
    patientAvailabilityToFilter?: { label: string; value: string }
) {
    const startDateValue = patientAvailabilityFromFilter?.value
    const endDateValue = patientAvailabilityToFilter?.value

    const startDate = startDateValue ? day(startDateValue) : undefined
    const endDate = endDateValue ? day(endDateValue) : undefined
    const isDateError = startDate && endDate && isAfter(startDate, endDate, 'day')

    return { startDate, endDate, isDateError }
}

export function isAvailableFrom(period: AvailabilityPeriod): period is { type: 'availableFrom'; startDate: Dayjs } {
    return period.type === 'availableFrom'
}

export function isUnavailableBetween(period: AvailabilityPeriod): period is { type: 'unavailableBetween'; startDate: Dayjs; endDate: Dayjs } {
    return period.type === 'unavailableBetween'
}
/**
 * @param endDate Inclusive
 */
export function isPatientAvailableOnAnyDayInDateRange(startDate: Dayjs, endDate: Dayjs, surgery: Surgery): boolean {
    if (getIsWaiting(surgery)) {
        return false
    }
    const patientAvailabilities = getPatientAvailabilityFromSurgery(surgery)

    if (patientAvailabilities.length === 0) {
        return true
    }

    const availabilityFrom = patientAvailabilities.find(isAvailableFrom)?.startDate
    if (availabilityFrom && isBefore(endDate, availabilityFrom, 'day')) {
        return false
    }

    const disjointUnavailabilities = toDisjointIntervals(patientAvailabilities.filter(isUnavailableBetween), 'day')
    return !disjointUnavailabilities.some(unavailablePeriod => containsInterval(unavailablePeriod, { startDate, endDate }, 'day'))
}

const MAX_DATE = day('3000-01-01')

export function isPatientAvailableOnEntireDateRange(startDate: Dayjs | undefined, endDate: Dayjs | undefined, surgery: Surgery): boolean {
    if (!startDate) {
        if (!endDate) {
            console.warn("At least one date should be provided to 'isPatientAvailableOnDateRange'")
            return true
        }
        return isPatientAvailableOnEntireDateRange(getToday(), endDate, surgery)
    }
    if (!endDate) {
        return isPatientAvailableOnEntireDateRange(startDate, MAX_DATE, surgery)
    }

    if (isAfter(startDate, endDate, 'day')) {
        return true // invalid date range
    }

    const patientAvailabilities = getPatientAvailabilityFromSurgery(surgery)

    if (patientAvailabilities.length === 0) {
        return true
    }

    if (getIsWaiting(surgery)) {
        return false
    }

    const availabilityFrom = patientAvailabilities.find(isAvailableFrom)?.startDate
    if (availabilityFrom && isBefore(startDate, availabilityFrom, 'day')) {
        return false
    }

    return patientAvailabilities.filter(isUnavailableBetween).every(unavailability => !overlaps({ startDate, endDate }, unavailability, 'day'))
}

export function isPatientAvailableOnDate(date: Dayjs, surgery: Surgery): boolean {
    return isPatientAvailableOnAnyDayInDateRange(date, date, surgery)
}
